// Provenance: repo:tds-reconciliation-portal:frontend/src/shell/workspaceSession.tsx
// (ADR-0005) — period/run ids became UUID strings; the reasoning carries whole.
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

import type { PeriodSummary, RawSummaryEntry, Upload } from '../api/types'

/** What this browser session has actually loaded, per period AND per side.
 *
 *  A fresh session (new tab, or a fresh login) starts empty: nothing on screen,
 *  badges at zero, both drop zones blank. A refresh mid-task does not throw the
 *  user back to the open-chooser.
 *
 *  Stage 57 made it per SIDE, and that is the whole point rather than a detail.
 *  One flag per period gated two independent uploads: uploading the 26AS set it,
 *  and the books card immediately revealed a ledger from a previous session — a
 *  file the user had not put there, on a screen promising "nothing is loaded by
 *  default". The run would have used it too.
 *
 *  Nothing is deleted to achieve this. The files stay on the server and stay in
 *  Upload History; this governs what the screen CLAIMS, not what exists.
 */
export type Side = 'source' | 'book'

interface WorkspaceSession {
  /** mark one side loaded; omit `side` when the whole period is being opened */
  open: (periodId: string, side?: Side) => void
  isOpen: (periodId: string, side: Side) => boolean
  /** both sides at once, for the badges and anything asking about the period */
  sides: (periodId: string) => Record<Side, boolean>
}

const KEY = 'tds.openedSides'

export function sideKey(periodId: string, side: Side): string {
  return `${periodId}:${side}`
}

/** Opening a previous run loads BOTH sides — it is an explicit act of loading
 *  that run's data, not an accident of having uploaded one file. */
export function sidesOf(periodId: string): string[] {
  return [sideKey(periodId, 'source'), sideKey(periodId, 'book')]
}

/** Which file a side's card should show.
 *
 *  `result` is what was just uploaded and always wins: the session flag is set
 *  in the same tick and there is no honest reason to blank a file the user is
 *  watching arrive.
 */
export function activeUpload(
  uploads: Upload[],
  kind: Side,
  loaded: boolean,
  result: Upload | null,
): Upload | null {
  if (result) return result
  if (!loaded) return null
  return uploads.find((u) => u.kind === kind && u.parse_status === 'parsed'
    && PARSES[kind].includes(u.parse_stats?.kind ?? '')) ?? null
}

/** the parse kinds each side accepts — native or profile-parsed */
const PARSES: Record<Side, readonly string[]> = {
  source: ['traces_26as', 'statement'],
  book: ['gl_ledger', 'ledger'],
}

/** The Raw Data count: only the sides loaded this session.
 *
 *  Falls back to the period total when the per-side split is absent, because a
 *  badge silently reading zero is worse than one reading a number that is
 *  honest about the period rather than about the session.
 */
export function loadedRows(
  summary: PeriodSummary | undefined,
  loaded: Record<Side, boolean>,
): number {
  if (!summary) return 0
  if (!loaded.source && !loaded.book) return 0
  const split = summary.raw_rows_by_kind
  if (!split) return summary.raw_rows ?? 0
  return (loaded.source ? (split.source ?? 0) : 0)
    + (loaded.book ? (split.book ?? 0) : 0)
}

/** Which raw-summary entries a surface may show.
 *
 *  Raw Data used the session as an all-or-nothing GATE and then summed every
 *  entry the period had: one upload let you in, and what you saw was both
 *  sides — 76,161 rows and a populated Book Data tab built from a ledger the
 *  session never loaded. A gate answers "may I look"; this answers "at what",
 *  and the second question is the one that was never asked.
 */
export function visibleRawEntries(
  entries: RawSummaryEntry[],
  loaded: Record<Side, boolean>,
): RawSummaryEntry[] {
  return entries.filter((e) => loaded[e.side === 'book' ? 'book' : 'source'])
}

/** The Reports badge counts the documents of the run THIS session loaded.
 *
 *  It used to show the latest complete run's document count behind an "either
 *  side loaded" gate, so uploading one file revealed a 3 that belonged to a
 *  run from a previous session. Zero reads as absent rather than as `0`: a
 *  badge is a count of something there, and a loaded run with no documents
 *  yet has nothing to point at.
 */
export function reportsBadge(
  runId: string | null,
  documents: number | undefined,
): number | undefined {
  if (runId == null || !documents) return undefined
  return documents
}

/** What the alias map is entitled to say about a group's membership.
 *
 *  Stage 59 made the map show company names instead of regexes, sourced from
 *  the latest complete run. On a fresh session that is someone else's run: the
 *  ledger names were on screen before any ledger had been loaded. The four
 *  answers are genuinely different and the screen must not merge them —
 *  especially `no-run` ("we cannot say yet") and `none` ("we checked, and this
 *  group matches nothing"), which is the dead-pattern warning Stage 59 added.
 */
export type CoversState =
  | { kind: 'no-session-run' }
  | { kind: 'no-run' }
  | { kind: 'none'; runId: string }
  | { kind: 'names'; names: string[]; runId: string }

export function coversState(
  names: string[] | null | undefined,
  sessionRun: string | null,
  coversRunId: string | null | undefined,
): CoversState {
  if (sessionRun == null) return { kind: 'no-session-run' }
  if (names == null || coversRunId == null) return { kind: 'no-run' }
  if (names.length === 0) return { kind: 'none', runId: coversRunId }
  return { kind: 'names', names, runId: coversRunId }
}

function load(): Set<string> {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch {
    /* sessionStorage unavailable / bad JSON — start empty */
  }
  return new Set()
}

function persist(s: Set<string>) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify([...s]))
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

const Ctx = createContext<WorkspaceSession | null>(null)

export function WorkspaceSessionProvider({ children }: { children: ReactNode }) {
  const [opened, setOpened] = useState<Set<string>>(load)
  const open = useCallback((periodId: string, side?: Side) => {
    const keys = side ? [sideKey(periodId, side)] : sidesOf(periodId)
    setOpened((s) => {
      if (keys.every((k) => s.has(k))) return s
      const n = new Set(s)
      for (const k of keys) n.add(k)
      persist(n)
      return n
    })
  }, [])
  const isOpen = useCallback(
    (periodId: string, side: Side) => opened.has(sideKey(periodId, side)),
    [opened],
  )
  const sides = useCallback(
    (periodId: string) => ({
      source: opened.has(sideKey(periodId, 'source')),
      book: opened.has(sideKey(periodId, 'book')),
    }),
    [opened],
  )
  return <Ctx.Provider value={{ open, isOpen, sides }}>{children}</Ctx.Provider>
}

export function useWorkspaceSession(): WorkspaceSession {
  const c = useContext(Ctx)
  if (!c) throw new Error('useWorkspaceSession must be used within WorkspaceSessionProvider')
  return c
}
